import React from 'react';
import { Box, Button, Heading, Text } from '@indeed/ifl-components';
import { Edit, FileDoc } from '@indeed/ifl-icons';
import GettextFactory from '../../../util/GettextFactory';
import { FileResumePreview } from '../components/FileResumePreview';
import { formatDateRangeDisplay } from './dateFormatUtils';
import type { GetAutoApplyReviewDataQuery } from './__generated__/ReviewPage.generated';

type ReviewJobSeekerProfile = NonNullable<
    NonNullable<GetAutoApplyReviewDataQuery['jobSeekerProfile']>['profile']
>;
export type ReviewIndeedResume = ReviewJobSeekerProfile['resume'][number];

/** Minimal shape shared by the V2 (`jobSeekerResumes`) and legacy (`fileInformation`) file resumes. */
export interface ReviewSelectedFileResume {
    id: string;
    fileName?: string | null;
    uploadTimestamp?: string | number | null;
}

export interface ResumePreviewSectionProps {
    /** Section heading (e.g. "Resume" or "How we autofill applications"). */
    heading: string;
    /** Optional icon rendered before the heading (e.g. the progressive-automation variant). */
    icon?: React.ReactNode;
    onEditResume: () => void;
    editDisabled?: boolean;
    showIndeedResume: boolean;
    /**
     * Whether the saved-file resume is the active selection. Distinct from
     * `!showIndeedResume` so a file is surfaced only when it is the actual
     * selection, not merely when Indeed is unselected (IAFE-2079).
     */
    showFileResume: boolean;
    indeedResume: ReviewIndeedResume | undefined;
    selectedFileResume: ReviewSelectedFileResume | null;
    pdfUrl: string | null;
    loadingPdf: boolean;
    locale: string;
}

/** Format date from timestamp */
const formatDate = (timestamp: string | number | null | undefined, locale: string): string => {
    if (!timestamp) return '';
    return new Intl.DateTimeFormat(locale, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    }).format(new Date(timestamp));
};

/**
 * Presentational resume preview (header + file/Indeed resume card) shared by the
 * legacy Review page and the progressive-automation "Customize your experience" variant.
 *
 * Renders a fragment so the caller controls the surrounding layout container.
 */
export const ResumePreviewSection: React.FC<ResumePreviewSectionProps> = ({
    heading,
    icon,
    onEditResume,
    editDisabled,
    showIndeedResume,
    showFileResume,
    indeedResume,
    selectedFileResume,
    pdfUrl,
    loadingPdf,
    locale,
}) => {
    const gt = GettextFactory();

    return (
        <>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                }}
            >
                {icon ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box data-testid="resume-heading-icon" sx={{ flexShrink: 0 }}>
                            {icon}
                        </Box>
                        <Heading level={3} as="h2" sx={{ marginBlockEnd: 0 }}>
                            {heading}
                        </Heading>
                    </Box>
                ) : (
                    <Heading level={3} as="h2" sx={{ marginBlockEnd: 0 }}>
                        {heading}
                    </Heading>
                )}
                <Button
                    onClick={onEditResume}
                    disabled={editDisabled}
                    aria-label={gt.gettext('Edit resume')}
                    variant="tertiary"
                    iconOnly
                >
                    <Edit size="md" variant="stroke" aria-hidden />
                </Button>
            </Box>

            {/* File Resume Preview */}
            {showFileResume && selectedFileResume && (
                <Box
                    data-dd-action-name="file resume preview"
                    sx={{
                        border: '1px solid',
                        borderColor: 'border.action.weak.hover',
                        borderRadius: 'lg',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                    }}
                >
                    {/* Resume File Info */}
                    <Box
                        sx={{
                            display: 'flex',
                            gap: 2,
                            alignItems: 'flex-start',
                            padding: 4,
                        }}
                    >
                        <Box sx={{ flexShrink: 0 }}>
                            <FileDoc size="md" aria-hidden />
                        </Box>
                        <Box
                            sx={{
                                flex: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 1,
                            }}
                        >
                            <Heading level={3} as="h3">
                                {selectedFileResume.fileName}
                            </Heading>
                            <Text level={2}>
                                {gt.gettext('Updated')}{' '}
                                {formatDate(Number(selectedFileResume.uploadTimestamp), locale)}
                            </Text>
                        </Box>
                    </Box>

                    {/* PDF Preview */}
                    <Box
                        sx={{
                            padding: 4,
                            paddingBlockStart: 0,
                        }}
                    >
                        <FileResumePreview
                            fileUrl={pdfUrl}
                            fileName={selectedFileResume.fileName ?? undefined}
                            loading={loadingPdf}
                            showBorder={true}
                        />
                    </Box>
                </Box>
            )}

            {/* Indeed Resume Preview */}
            {showIndeedResume && indeedResume && (
                <Box
                    sx={{
                        border: '1px solid',
                        borderColor: 'border.weak',
                        borderRadius: 'lg',
                        padding: 4,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 4,
                    }}
                >
                    {/* Contact Details Section */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Heading level={3} as="h3" sx={{ marginBlockEnd: 0 }}>
                            {indeedResume.firstName} {indeedResume.lastName}
                        </Heading>
                        {indeedResume.headline && <Text level={2}>{indeedResume.headline}</Text>}
                        {indeedResume.phoneNumber && (
                            <Text level={2}>{indeedResume.phoneNumber}</Text>
                        )}
                        {indeedResume.location?.formattedLocation && (
                            <Text level={2}>{indeedResume.location.formattedLocation}</Text>
                        )}
                    </Box>

                    {/* Professional Summary */}
                    {indeedResume.summary && (
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 2,
                            }}
                        >
                            <Heading level={3} as="h3" sx={{ marginBlockEnd: 0 }}>
                                {gt.gettext('Professional summary')}
                            </Heading>
                            <Text level={2} variant="secondary" sx={{ whiteSpace: 'pre-line' }}>
                                {indeedResume.summary}
                            </Text>
                        </Box>
                    )}

                    {/* Work Experience */}
                    {indeedResume.workExperiences && indeedResume.workExperiences.length > 0 && (
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 2,
                            }}
                        >
                            <Heading level={3} as="h3" sx={{ marginBlockEnd: 0 }}>
                                {gt.gettext('Work experience')}
                            </Heading>
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 4,
                                }}
                            >
                                {indeedResume.workExperiences.map((exp, index) => (
                                    <Box
                                        key={index}
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 1,
                                        }}
                                    >
                                        <Heading level={2} variant="subheading" as="h4">
                                            {exp.title}
                                        </Heading>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                            }}
                                        >
                                            <Text level={2}>{exp.company}</Text>
                                            {exp.location?.formattedLocation && (
                                                <>
                                                    <Text level={3} variant="secondary">
                                                        •
                                                    </Text>
                                                    <Text level={2}>
                                                        {exp.location.formattedLocation}
                                                    </Text>
                                                </>
                                            )}
                                        </Box>
                                        {exp.dateRange && (
                                            <Text level={1} variant="secondary">
                                                {formatDateRangeDisplay(
                                                    exp.dateRange,
                                                    locale,
                                                    gt.gettext('Present'),
                                                    gt.gettext(' – '),
                                                )}
                                            </Text>
                                        )}
                                        {exp.description && (
                                            <Text
                                                level={2}
                                                sx={{
                                                    whiteSpace: 'pre-line',
                                                }}
                                            >
                                                {exp.description}
                                            </Text>
                                        )}
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    )}

                    {/* Education */}
                    {indeedResume.educations && indeedResume.educations.length > 0 && (
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 2,
                            }}
                        >
                            <Heading level={3} as="h3" sx={{ marginBlockEnd: 0 }}>
                                {gt.gettext('Education')}
                            </Heading>
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 2,
                                }}
                            >
                                {indeedResume.educations.map((edu, index) => (
                                    <Box
                                        key={index}
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: '2px',
                                        }}
                                    >
                                        <Heading level={2} variant="subheading" as="h4">
                                            {edu.degree[0]?.degreeName}
                                            {edu.degree[0]?.field && ` in ${edu.degree[0].field}`}
                                        </Heading>
                                        <Text level={2} variant="secondary">
                                            {edu.school}
                                            {edu.location?.formattedLocation &&
                                                `, ${edu.location.formattedLocation}`}
                                        </Text>
                                        {edu.dateRange && (
                                            <Text level={1} variant="secondary">
                                                {formatDateRangeDisplay(
                                                    edu.dateRange,
                                                    locale,
                                                    gt.gettext('Present'),
                                                    ' - ',
                                                )}
                                            </Text>
                                        )}
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    )}

                    {/* Skills */}
                    {indeedResume.skills && indeedResume.skills.length > 0 && (
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 2,
                            }}
                        >
                            <Heading level={3} as="h3" sx={{ marginBlockEnd: 0 }}>
                                {gt.gettext('Skills')}
                            </Heading>
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 2,
                                }}
                            >
                                {indeedResume.skills.map((skill, index) => (
                                    <Text key={index} level={2}>
                                        {skill.taxonomyConceptTitle?.label}
                                    </Text>
                                ))}
                            </Box>
                        </Box>
                    )}

                    {/* Certifications */}
                    {indeedResume.certifications && indeedResume.certifications.length > 0 && (
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 2,
                            }}
                        >
                            <Heading level={3} as="h3" sx={{ marginBlockEnd: 0 }}>
                                {gt.gettext('Certifications and licenses')}
                            </Heading>
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 6,
                                }}
                            >
                                {indeedResume.certifications.map((cert, index) => (
                                    <Box
                                        key={index}
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: '2px',
                                        }}
                                    >
                                        <Heading level={2} variant="subheading" as="h4">
                                            {cert.title}
                                        </Heading>
                                        {cert.description && (
                                            <Text
                                                level={2}
                                                sx={{
                                                    whiteSpace: 'pre-line',
                                                }}
                                            >
                                                {cert.description}
                                            </Text>
                                        )}
                                        {cert.dateRange && (
                                            <Text level={1} variant="secondary">
                                                {formatDateRangeDisplay(
                                                    cert.dateRange,
                                                    locale,
                                                    gt.gettext('Present'),
                                                    ' - ',
                                                )}
                                            </Text>
                                        )}
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    )}

                    {/* Languages */}
                    {indeedResume.languageSkills && indeedResume.languageSkills.length > 0 && (
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 2,
                            }}
                        >
                            <Heading level={3} as="h3" sx={{ marginBlockEnd: 0 }}>
                                {gt.gettext('Languages')}
                            </Heading>
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '2px',
                                }}
                            >
                                {indeedResume.languageSkills.map((lang, index) => (
                                    <Box
                                        key={index}
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: '2px',
                                        }}
                                    >
                                        <Heading level={2} variant="subheading" as="h4">
                                            {lang.language}
                                        </Heading>
                                        {lang.proficiency && (
                                            <Text level={2}>{lang.proficiency}</Text>
                                        )}
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    )}
                </Box>
            )}
        </>
    );
};
